import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createScript, updateScript, formatScript, uploadScriptFile } from "@/services/scriptService";
import { SUPPORTED_EXTENSIONS } from "@/services/fileParserService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { IconScript } from "@/components/Icons";
import { Maximize2, Minimize2, Save, Wand2, Loader2, FileText, Clock, Sparkles, Upload, File, X, PanelRightOpen, PanelRightClose, Image, ChevronRight } from "lucide-react";

const ACCEPT_STRING = SUPPORTED_EXTENSIONS.join(',') +
  ',.doc,application/pdf,application/msword,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

import { getShotAnchors, getConceptsForShot, getConceptImageUrl, getShotImageUrl } from "@/services/shotService";
import { ScrollArea } from "@/components/ui/scroll-area";

function ShotSidePanel({ projectId, scenes, conceptLayerEnabled, onNavigateToStoryboard }) {
  const [selectedScene, setSelectedScene] = useState(scenes.length > 0 ? 1 : null);
  const [anchors, setAnchors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [conceptCounts, setConceptCounts] = useState({});

  useEffect(() => {
    if (!selectedScene || !projectId) return;
    let cancelled = false;
    setLoading(true);
    getShotAnchors(projectId, selectedScene)
      .then(async (data) => {
        if (cancelled) return;
        setAnchors(data || []);
        // Fetch concept counts for each anchor if concept layer enabled
        if (conceptLayerEnabled && data?.length > 0) {
          const counts = {};
          await Promise.all(
            data.map(async (a) => {
              try {
                const concepts = await getConceptsForShot(projectId, a.id);
                counts[a.id] = concepts?.length || 0;
              } catch { counts[a.id] = 0; }
            })
          );
          if (!cancelled) setConceptCounts(counts);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedScene, projectId, conceptLayerEnabled]);

  return (
    <div className="h-full flex flex-col">
      {/* Scene selector */}
      <div className="px-3 py-2 border-b border-[var(--border-subtle)] flex-shrink-0">
        <label className="text-[9px] text-[var(--text-disabled)] uppercase tracking-wider mb-1 block">Scene</label>
        <div className="flex gap-1 flex-wrap">
          {scenes.map((_, i) => (
            <button
              key={i + 1}
              onClick={() => setSelectedScene(i + 1)}
              className={`w-7 h-7 rounded text-[10px] font-medium transition-colors ${
                selectedScene === i + 1
                  ? "bg-[var(--color-gold-500)]/20 text-[var(--color-gold-400)] border border-[var(--color-gold-500)]/30"
                  : "bg-[var(--bg-overlay)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Anchors list */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 size={16} className="animate-spin text-[var(--text-disabled)]" />
            </div>
          )}

          {!loading && anchors.length === 0 && (
            <p className="text-[10px] text-[var(--text-disabled)] text-center py-8">
              No shots in Scene {selectedScene}
            </p>
          )}

          {anchors.map((anchor) => {
            const firstShot = anchor.shots?.[0];
            const count = conceptCounts[anchor.id] || anchor.shots?.length || 0;
            return (
              <button
                key={anchor.id}
                onClick={() => onNavigateToStoryboard?.(anchor)}
                className="w-full text-left rounded-xl border border-[var(--border-subtle)] hover:border-[var(--color-gold-500)]/30 transition-colors overflow-hidden group"
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-[var(--bg-muted)] relative overflow-hidden">
                  {firstShot ? (
                    <img
                      src={getShotImageUrl(anchor.id, firstShot.id)}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      crossOrigin="use-credentials"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image size={16} className="text-[var(--text-disabled)]" />
                    </div>
                  )}
                  {count > 0 && (
                    <span className="absolute top-1.5 right-1.5 text-[8px] px-1.5 py-0.5 rounded-full bg-black/60 text-white/80 backdrop-blur-sm">
                      {count} {conceptLayerEnabled ? 'concept' : 'shot'}{count !== 1 ? 's' : ''}
                    </span>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <ChevronRight size={20} className="text-white opacity-0 group-hover:opacity-70 transition-opacity" />
                  </div>
                </div>
                {/* Text */}
                <div className="p-2">
                  <p className="text-[10px] text-[var(--text-muted)] line-clamp-2">
                    {anchor.selected_text?.slice(0, 100) || "Shot anchor"}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}

export default function ScriptPage({ projectId, project, script, onUpdate }) {
  const [content, setContent] = useState(script?.content || "");
  const [format, setFormat] = useState(script?.format || "screenplay");
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formatting, setFormatting] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showShotPanel, setShowShotPanel] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  const scenes = script?.scenes || [];
  const conceptLayerEnabled = project?.concept_layer_enabled || false;

  useEffect(() => {
    setContent(script?.content || "");
    setFormat(script?.format || "screenplay");
  }, [script]);

  useEffect(() => {
    setHasChanges(content !== (script?.content || ""));
  }, [content, script]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (hasChanges) handleSave();
      }
      if (e.key === 'Escape' && focusMode) {
        setFocusMode(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasChanges, focusMode]);

  // Drag-and-drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer?.types?.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    const files = e.dataTransfer?.files;
    if (files?.length > 0) {
      processFile(files[0]);
    }
  }, [projectId, script]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const processFile = async (file) => {
    // Validate file size
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large', { description: 'Maximum file size is 50MB' });
      return;
    }

    // Validate extension
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    const validExts = [...SUPPORTED_EXTENSIONS, '.doc'];
    if (!validExts.includes(ext)) {
      toast.error('Unsupported format', {
        description: `Supported: PDF, DOCX, Final Draft, Fountain, TXT, RTF, Celtx, Highland, Fade In, OSF`
      });
      return;
    }

    setUploading(true);
    const toastId = toast.loading(`Parsing ${file.name}...`);

    try {
      const result = await uploadScriptFile(file, projectId, script?.id);
      const text = result.content || result.text || '';

      if (text) {
        setContent(text);
        setFormat("screenplay");
      }

      const parts = [];
      if (result.format_detected) parts.push(result.format_detected.toUpperCase());
      if (result.page_count) parts.push(`${result.page_count} pages`);
      if (result.scene_count) parts.push(`${result.scene_count} scenes`);
      if (result.character_count) parts.push(`${Math.round(result.character_count / 1000)}K chars`);

      toast.success('Script imported!', {
        id: toastId,
        description: parts.join(' · ') || 'File parsed successfully',
      });

      onUpdate();
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Import failed', {
        id: toastId,
        description: err.response?.data?.detail || err.message || 'Please try a different file',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (script) {
        await updateScript(script.id, { content, format });
      } else {
        await createScript({ project_id: projectId, content, format });
      }
      toast.success("Script saved");
      setHasChanges(false);
      onUpdate();
    } catch (error) {
      toast.error("Failed to save script");
    } finally {
      setSaving(false);
    }
  };

  const handleFormat = async () => {
    if (!content.trim()) return;

    setFormatting(true);
    const toastId = toast.loading("Formatting script...");

    try {
      const data = await formatScript(content);
      setContent(data.formatted_content);
      setFormat("screenplay");

      const sceneCount = data.scene_count || 0;

      toast.success(`Formatting Complete!`, {
        id: toastId,
        description: `Detected ${sceneCount} scene${sceneCount !== 1 ? 's' : ''}`
      });
    } catch (error) {
      toast.error("Formatting failed", {
        id: toastId,
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setFormatting(false);
    }
  };

  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
  const pageEstimate = Math.ceil(wordCount / 250);

  // Check if content looks like a formatted screenplay
  const hasSceneHeadings = /^(INT\.|EXT\.)/im.test(content);
  const needsFormatting = content.length > 100 && !hasSceneHeadings;

  // Hidden file input
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={ACCEPT_STRING}
      onChange={handleFileSelect}
      className="hidden"
    />
  );

  // Focus Mode
  if (focusMode) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-[var(--bg-app)] flex items-center justify-center"
      >
        {fileInput}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(circle at center, transparent 30%, rgba(0,0,0,0.4) 100%)'
        }} />

        <motion.button
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => setFocusMode(false)}
          className="absolute top-6 right-6 btn-ghost flex items-center gap-2"
        >
          <Minimize2 size={18} />
          <span>Exit Focus</span>
          <span className="text-xs text-[var(--text-disabled)] ml-2">Esc</span>
        </motion.button>

        <div className="w-full max-w-3xl h-[calc(100vh-120px)] flex flex-col px-6">
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="flex-1 bg-[#fafaf9] rounded-2xl shadow-2xl overflow-hidden relative"
            style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.1)' }}
          >
            <div className="absolute inset-0 opacity-[0.02]" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            }} />

            <Textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="INT. COFFEE SHOP - DAY

The morning light filters through dusty windows..."
              className="w-full h-full border-none bg-transparent text-[var(--color-base-800)] font-mono text-base leading-[2] p-12 resize-none focus:ring-0 focus:outline-none"
              style={{ caretColor: 'var(--color-gold-600)' }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6 flex items-center justify-between"
          >
            <div className="flex items-center gap-6 text-sm text-[var(--text-muted)]">
              <span className="flex items-center gap-2">
                <FileText size={14} />
                {wordCount.toLocaleString()} words
              </span>
              <span className="flex items-center gap-2">
                <Clock size={14} />
                ~{pageEstimate} pages
              </span>
            </div>
            <div className="flex items-center gap-3">
              {hasChanges && (
                <span className="text-sm text-[var(--color-gold-400)]">Unsaved changes</span>
              )}
              <Button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                <span className="ml-2">Save</span>
                <span className="ml-2 text-xs opacity-70">⌘S</span>
              </Button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Empty state — show upload CTA
  if (!content && !script?.content) {
    return (
      <div
        className="h-full flex flex-col"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {fileInput}

        {/* Drag overlay */}
        <AnimatePresence>
          {isDragging && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-50 flex items-center justify-center bg-[var(--bg-app)]/90 backdrop-blur-sm border-2 border-dashed border-[var(--color-gold-400)] rounded-lg"
            >
              <div className="text-center">
                <Upload size={48} className="mx-auto text-[var(--color-gold-400)] mb-4" />
                <p className="text-lg font-semibold text-[var(--text-primary)]">Drop your script file</p>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  PDF, DOCX, Final Draft, Fountain, and more
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-lg mx-auto text-center px-6"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[var(--color-gold-500)]/10 flex items-center justify-center">
              <FileText size={28} className="text-[var(--color-gold-400)]" />
            </div>

            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">
              Add Your Script
            </h2>
            <p className="text-sm text-[var(--text-muted)] mb-8">
              Upload a file or paste your script directly. We support all major screenplay formats.
            </p>

            {/* Upload button */}
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-[var(--color-gold-500)] hover:bg-[var(--color-gold-600)] text-black font-semibold px-8 py-2.5 mb-4"
            >
              {uploading ? (
                <Loader2 size={16} className="animate-spin mr-2" />
              ) : (
                <Upload size={16} className="mr-2" />
              )}
              {uploading ? 'Importing...' : 'Upload Script File'}
            </Button>

            <p className="text-xs text-[var(--text-disabled)] mb-6">
              or drag & drop a file anywhere on this page
            </p>

            {/* Supported formats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-left">
              {[
                { ext: 'PDF', desc: 'Adobe PDF' },
                { ext: 'DOCX', desc: 'Word' },
                { ext: 'FDX', desc: 'Final Draft' },
                { ext: 'Fountain', desc: 'Plain text' },
                { ext: 'TXT', desc: 'Text file' },
                { ext: 'RTF', desc: 'Rich Text' },
                { ext: 'Celtx', desc: 'Celtx' },
                { ext: 'Highland', desc: 'Highland' },
                { ext: 'Fade In', desc: '.fadein' },
                { ext: 'OSF', desc: 'Open Format' },
              ].map((f) => (
                <div key={f.ext} className="p-2 rounded-md bg-[var(--bg-elevated)] border border-[var(--border-subtle)] text-center">
                  <p className="text-[11px] font-semibold text-[var(--text-primary)]">{f.ext}</p>
                  <p className="text-[9px] text-[var(--text-muted)]">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-[var(--border-subtle)]">
              <button
                onClick={() => {
                  // Switch to editor mode by setting empty content
                  setContent(' ');
                  setTimeout(() => setContent(''), 0);
                }}
                className="text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                Or type / paste directly in the editor
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-full flex flex-col relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {fileInput}

      {/* Drag overlay (when editor has content) */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center bg-[var(--bg-app)]/90 backdrop-blur-sm border-2 border-dashed border-[var(--color-gold-400)] rounded-lg"
          >
            <div className="text-center">
              <Upload size={48} className="mx-auto text-[var(--color-gold-400)] mb-4" />
              <p className="text-lg font-semibold text-[var(--text-primary)]">Replace script</p>
              <p className="text-sm text-[var(--text-muted)] mt-1">Drop to import new script file</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Uploading overlay */}
      <AnimatePresence>
        {uploading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center bg-[var(--bg-app)]/80 backdrop-blur-sm"
          >
            <div className="text-center">
              <Loader2 size={32} className="mx-auto animate-spin text-[var(--color-gold-400)] mb-3" />
              <p className="text-sm font-medium text-[var(--text-primary)]">Parsing script file...</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Format Banner */}
      <AnimatePresence>
        {needsFormatting && !formatting && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-gradient-to-r from-[var(--color-gold-500)]/10 to-[var(--color-teal-500)]/10 border-b border-[var(--color-gold-500)]/20 overflow-hidden"
          >
            <div className="px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[var(--color-gold-500)]/20 flex items-center justify-center">
                  <Sparkles size={16} className="text-[var(--color-gold-400)]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">Format your script</p>
                  <p className="text-xs text-[var(--text-muted)]">Click Format to structure your script and detect scenes automatically.</p>
                </div>
              </div>
              <Button
                onClick={handleFormat}
                className="btn-primary"
              >
                <Wand2 size={16} />
                <span className="ml-2">Format Script</span>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toolbar */}
      <div className="h-14 px-6 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-6 text-sm text-[var(--text-muted)]">
          <span className="flex items-center gap-2">
            <FileText size={14} />
            {wordCount.toLocaleString()} words
          </span>
          <span className="flex items-center gap-2">
            <Clock size={14} />
            ~{pageEstimate} pages
          </span>
          {scenes.length > 0 && (
            <span className="text-[var(--color-gold-400)]">
              {scenes.length} scenes
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {scenes.length > 0 && (
            <Button
              onClick={() => setShowShotPanel((v) => !v)}
              variant="ghost"
              className={`btn-ghost ${showShotPanel ? 'text-[var(--color-gold-400)]' : ''}`}
            >
              {showShotPanel ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
              <span className="ml-2">Shots</span>
            </Button>
          )}

          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            variant="ghost"
            className="btn-ghost"
          >
            <Upload size={16} />
            <span className="ml-2">Upload</span>
          </Button>

          <Button
            onClick={() => setFocusMode(true)}
            variant="ghost"
            className="btn-ghost"
          >
            <Maximize2 size={16} />
            <span className="ml-2">Focus</span>
          </Button>

          <Button
            onClick={handleFormat}
            disabled={formatting || !content.trim()}
            className="btn-secondary"
          >
            {formatting ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
            <span className="ml-2">Format</span>
          </Button>

          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="btn-primary"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            <span className="ml-2">Save</span>
          </Button>
        </div>
      </div>

      {/* Editor Area + Optional Shot Panel */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 relative overflow-hidden">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Paste or type your script here...

You can write in any format - novel style, rough draft, or proper screenplay.
Click 'Format' to convert it into professional screenplay format.

Example:
John walked into the coffee shop. It was early morning and the place was nearly empty.
'I'm looking for someone,' he said to the barista."
            className="w-full h-full bg-[var(--bg-surface)] border-none font-mono text-sm leading-loose p-8 resize-none focus:ring-0 focus:outline-none text-[var(--text-primary)] placeholder:text-[var(--text-disabled)]"
            style={{ caretColor: 'var(--color-gold-400)' }}
          />
        </div>

        {/* Shot Side Panel */}
        <AnimatePresence>
          {showShotPanel && scenes.length > 0 && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 280, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="flex-shrink-0 border-l border-[var(--border-subtle)] bg-[var(--bg-surface)] overflow-hidden"
            >
              <div className="w-[280px] h-full">
                <ShotSidePanel
                  projectId={projectId}
                  scenes={scenes}
                  conceptLayerEnabled={conceptLayerEnabled}
                  onNavigateToStoryboard={(anchor) => {
                    // Navigate to storyboard tab with this anchor focused
                    // Use the window-level tab switcher if available
                    if (window.__switchTab) {
                      window.__switchTab("storyboard", { anchorId: anchor.id, sceneNumber: anchor.scene_number });
                    }
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
